<template>
  <app-button
    :color="color"
    data-cy-registration-request-cta
    :size="size"
    :variant="variant"
    @click="onClick"
  >
    {{ label }}
  </app-button>

  <registration-request-overview
    v-if="list"
    class="rounded bg-primary-50 px-2 py-1 text-xs"
    :list="list"
  />
</template>

<script lang="ts" setup>
import type {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from "@pollen/ui-library";

import { useDialog, useToastNotification } from "#imports";
import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";
import RegistrationRequestOverview from "~/business-areas/registration-request/components/registration-request-overview.vue";
import type { RegistrationRequestView } from "~~/model/organization.model";

const properties = defineProps<{
  course: CoursePageView;
  session: CourseSessionView;
  size?: ButtonSize;
  color?: ButtonColor;
  variant?: ButtonVariant;
  block?: boolean;
}>();

const { list, refresh } = await useRegistrationRequestList({
  session_id: properties.session.id,
  status: ["ACCEPTED", "PENDING"],
});

const { hasRole } = useOrganizationMemberProfile();

const { t } = useI18n();
const label = computed(() => {
  if (properties.size === "small") {
    return hasRole("OWNER")
      ? t("registration_request.cta.as_owner")
      : t("registration_request.cta_short.as_member");
  }

  if (hasRole("OWNER") && list.value?.length)
    return t("registration_request.cta.as_owner_add_more");

  return hasRole("OWNER")
    ? t("registration_request.cta.as_owner")
    : t("registration_request.cta.as_member");
});

const registrationRequestDialog = useDialog("registrationRequestDialog");

function onClick() {
  registrationRequestDialog.open({
    session: properties.session,
    course: properties.course,
    onSubmit: onRegistrationDialogSubmit,
  });
}

const postRegistrationRequestDialog = useDialog(
  "postRegistrationRequestDialog",
);

const formattedSessionStartDate = useI18nDate(properties.session.start_date!);
function onRegistrationDialogSubmit(requests: RegistrationRequestView[]) {
  if (!hasRole("OWNER")) return;

  const isOwnerWithPendingRequests = requests.some(
    (r) => r.status === "PENDING",
  );

  if (isOwnerWithPendingRequests) {
    return postRegistrationRequestDialog.open({
      requests,
    });
  }

  useToastNotification().showSuccess(
    t("registration_request.confirmation_message.as_owner", {
      date: formattedSessionStartDate.value,
      count: requests.length,
    }),
  );
}

if (import.meta.client) {
  const { $on } = useNuxtApp();
  $on(
    "registration_request_created",
    ({
      session_id,
    }: {
      session_id: DatabaseTable<"course_sessions">["id"];
    }) => {
      if (session_id === properties.session.id) refresh();
    },
  );
}
</script>
